import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useScrollLinked } from "@/hooks/useScrollLinked";
import { useCardSpotlight } from "@/hooks/useCardSpotlight";

import ScreenshotLightbox from "@/components/ScreenshotLightbox";
import { useLocale } from "@/i18n/LocaleProvider";
import { useReferences } from "@/hooks/usePublicContent";
import type { PublicReference } from "@/data/references";
import { referenceIcon } from "@/lib/referenceIcons";
import { assetImage } from "@/lib/media";
import { ArrowRight } from "lucide-react";

const StackBadge = ({ label }: { label: string }) => (
  <span className="font-mono text-[10px] tracking-wide px-2.5 py-1 rounded-full border border-border bg-secondary/60 text-muted-foreground transition-colors duration-200 group-hover:border-accent/30">
    {label}
  </span>
);

const Spotlight = ({ size = 420 }: { size?: number }) => (
  <div
    className="pointer-events-none absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
    style={{
      background: `radial-gradient(${size}px circle at var(--mx) var(--my), hsl(var(--accent) / 0.12), transparent 65%)`,
    }}
  />
);

const FeaturedCard = ({ p }: { p: PublicReference }) => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const { ref, onMouseMove } = useCardSpotlight();
  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      className="group relative flex flex-col justify-end h-full min-h-[380px] md:min-h-0 rounded-3xl border border-border bg-card p-5 md:p-10 overflow-hidden transition-[transform,border-color] duration-200 hover:border-accent/40 hover:-translate-y-1"
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none transition-opacity duration-500 group-hover:opacity-30"
        style={{ background: "radial-gradient(circle at top right, hsl(var(--primary)) 0%, transparent 60%)" }}
      />
      <div className="absolute inset-0 tech-dots opacity-30 pointer-events-none" />

      {/* First gallery screenshot as a tilted, grayscale backdrop fading toward the bottom-left — sits below the glow layers */}
      {p.content.screenshots[0] && (
        <div
          aria-hidden
          className="hidden md:block absolute inset-0 z-0 pointer-events-none overflow-hidden rounded-3xl"
          style={{ perspective: "1600px" }}
        >
          <img
            src={assetImage(p.content.screenshots[0].src, 1100, { quality: 70 }).src}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover object-top opacity-[0.14] transition-opacity duration-500 group-hover:opacity-[0.20]"
            style={{
              filter: "grayscale(1) contrast(1.05)",
              transform: "rotateY(-13deg) rotateX(6deg) rotateZ(-2deg) scale(1.35)",
              transformOrigin: "top right",
              WebkitMaskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 80%)",
              maskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 80%)",
            }}
          />
        </div>
      )}

      <Spotlight size={520} />

      {/* Whole-card link when a case study exists */}
      {p.has_case_study && (
        <Link
          to={localize(`/references/${p.slug}`)}
          aria-label={p.content.title}
          className="absolute inset-0 z-10"
        />
      )}

      {p.logo_url && (
        <div className="absolute top-5 right-5 md:top-10 md:right-10 z-10">
          <img
            {...assetImage(p.logo_url, 220, { quality: 82, jpeg: false })}
            alt={`${p.content.client || p.content.title} logo`}
            className={`h-8 md:h-11 w-auto opacity-90 ${p.logo_invert ? "brightness-0 invert" : ""}`}
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      <div className={`relative z-20 ${p.has_case_study ? "pointer-events-none" : ""}`}>
        <p className="font-mono text-xs tracking-widest text-accent uppercase mb-4">/{p.content.eyebrow}</p>
        <h3 className="font-bold text-foreground text-[1.55rem] sm:text-3xl md:text-5xl mb-5 leading-tight">{p.content.title}</h3>
        <p className="text-muted-foreground text-base md:text-lg max-w-xl mb-7 leading-relaxed">{p.content.description}</p>

        {/* Screenshot thumbnails → lightbox */}
        {p.content.screenshots.length > 0 && (
          <div className="mb-7 max-w-xl pointer-events-auto" onMouseMove={(e) => e.stopPropagation()}>
            <ScreenshotLightbox images={p.content.screenshots} layout="strip" />
          </div>
        )}

        <div className="flex flex-wrap gap-2.5 mb-8">
          {p.tags.map((s) => (
            <StackBadge key={s} label={s} />
          ))}
        </div>

        {p.has_case_study && (
          <span className="hover-glow-accent inline-block pointer-events-auto relative">

            <Link
              to={localize(`/references/${p.slug}`)}
              className="inline-flex items-center gap-2 px-6 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-sm transition-transform duration-200 active:scale-[0.97] group/btn"
            >
              {t("showcase.caseStudyCta")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </span>
        )}
      </div>
    </div>
  );
};

const MediumCard = ({ p }: { p: PublicReference }) => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const { ref, onMouseMove } = useCardSpotlight();
  const Icon = referenceIcon(p.icon);
  const shot = p.content.screenshots[0];
  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      className="group relative flex flex-col h-full min-h-[200px] rounded-3xl border border-border bg-card p-5 md:p-8 overflow-hidden transition-[transform,border-color] duration-200 hover:border-accent/40 hover:-translate-y-1"
    >
      {/* First screenshot as a faded, tilted grayscale backdrop */}
      {shot ? (
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none overflow-hidden rounded-3xl"
          style={{ perspective: "1400px" }}
        >
          <img
            src={assetImage(shot.src, 760, { quality: 68 }).src}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover object-top opacity-[0.12] transition-opacity duration-500 group-hover:opacity-[0.20]"
            style={{
              filter: "grayscale(1) contrast(1.05)",
              transform: "rotateY(-13deg) rotateX(6deg) rotateZ(-2deg) scale(1.35)",
              transformOrigin: "top right",
              WebkitMaskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 82%)",
              maskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 82%)",
            }}
          />
        </div>
      ) : (
        <div className="absolute top-0 right-0 p-6 opacity-[0.07] pointer-events-none transition-transform duration-500 group-hover:scale-110">
          <Icon size={96} className="text-accent" strokeWidth={1} />
        </div>
      )}

      {/* Logo top-right (only when a logo exists) */}
      {p.logo_url ? (
        <div className="absolute top-5 right-5 md:top-8 md:right-8 z-10">
          <img
            {...assetImage(p.logo_url, 160, { quality: 82, jpeg: false })}
            alt={`${p.content.client || p.content.title} logo`}
            className={`h-6 md:h-7 w-auto opacity-90 ${p.logo_invert ? "brightness-0 invert" : ""}`}
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : null}

      <Spotlight />

      {/* Whole-card link when a case study exists */}
      {p.has_case_study && (
        <Link
          to={localize(`/references/${p.slug}`)}
          aria-label={p.content.title}
          className="absolute inset-0 z-10"
        />
      )}

      <div className={`relative z-20 flex flex-col h-full ${p.has_case_study ? "pointer-events-none" : ""}`}>
        <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-4">/{p.content.eyebrow}</p>
        <h3 className="font-bold text-foreground text-2xl mb-3">{p.content.title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">{p.content.description}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {p.tags.map((s) => (
            <StackBadge key={s} label={s} />
          ))}
        </div>
        {p.has_case_study && (
          <Link
            to={localize(`/references/${p.slug}`)}
            className="pointer-events-auto relative z-20 mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors hover:text-accent/80 group/btn"
          >
            {t("showcase.caseStudyCta")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </Link>
        )}
      </div>

    </div>
  );
};

const SmallCard = ({ p }: { p: PublicReference }) => {
  const { localize } = useLocale();
  const { ref, onMouseMove } = useCardSpotlight();
  const Icon = referenceIcon(p.icon);
  const shot = p.content.screenshots[0];
  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      className="group relative flex flex-col justify-between h-full min-h-[160px] rounded-3xl border border-border bg-card p-5 md:p-7 overflow-hidden transition-[transform,border-color] duration-200 hover:border-primary/50 hover:-translate-y-1"
    >
      {/* First screenshot as a faded, tilted grayscale backdrop */}
      {shot && (
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none overflow-hidden rounded-3xl"
          style={{ perspective: "1400px" }}
        >
          <img
            src={assetImage(shot.src, 640, { quality: 66 }).src}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover object-top opacity-[0.10] transition-opacity duration-500 group-hover:opacity-[0.18]"
            style={{
              filter: "grayscale(1) contrast(1.05)",
              transform: "rotateY(-13deg) rotateX(6deg) rotateZ(-2deg) scale(1.35)",
              transformOrigin: "top right",
              WebkitMaskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 82%)",
              maskImage:
                "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 22%, hsl(0 0% 0% / 0) 82%)",
            }}
          />
        </div>
      )}
      <Spotlight size={340} />

      {/* Whole-card link when a case study exists */}
      {p.has_case_study && (
        <Link
          to={localize(`/references/${p.slug}`)}
          aria-label={p.content.title}
          className="absolute inset-0 z-10"
        />
      )}

      <div className={`relative z-20 flex flex-col h-full ${p.has_case_study ? "pointer-events-none" : ""}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-accent uppercase mb-1.5">/{p.content.eyebrow}</p>
            <h4 className="font-bold text-foreground text-xl">{p.content.title}</h4>
          </div>
          {p.logo_url ? (
            <img
              {...assetImage(p.logo_url, 140, { quality: 82, jpeg: false })}
              alt={`${p.content.client || p.content.title} logo`}
              className={`h-6 w-auto shrink-0 opacity-90 ${p.logo_invert ? "brightness-0 invert" : ""}`}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-9 h-9 shrink-0 rounded-lg bg-secondary flex items-center justify-center group-hover:bg-accent/15 transition-colors duration-200">
              <Icon size={16} className="text-muted-foreground group-hover:text-accent transition-colors duration-200" />
            </div>
          )}
        </div>
        <p className="text-muted-foreground text-sm mt-4 mb-5">{p.content.description}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {p.tags.map((s) => (
            <StackBadge key={s} label={s} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ShowcaseSection = () => {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { ref, progress } = useScrollLinked(0.9, 0.5);
  const { data: references = [] } = useReferences(lang);

  // Bento layout: one big featured card, then two medium and the rest small,
  // preserving the current visual hierarchy while ordering is admin-controlled.
  const featured = references.find((r) => r.featured) ?? references[0];
  const rest = references.filter((r) => r !== featured);
  const medium = rest.slice(0, 2);
  const small = rest.slice(2);

  return (
    <section id="work" className="section-y-wide bg-secondary/30 relative overflow-hidden">
      <div className="container relative">
        <div ref={ref} className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10 mb-14 md:mb-16">
          <div className="max-w-2xl">
            <ScrollReveal>
              <p className="mono-label mb-3">// selected_work</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                {t("showcase.title")}
              </h2>
              <p className="mt-5 text-muted-foreground text-[15px] sm:text-lg leading-relaxed">
                {t("showcase.subtitle")}
              </p>
              <div className="mt-6 h-[3px] w-24 bg-border rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${progress * 100}%`, background: "var(--gradient-primary)" }} />
              </div>
            </ScrollReveal>
          </div>

          {/* No star rating here: we don't display a rating we can't substantiate. */}

        </div>

        {featured && (
          <div className="grid grid-cols-1 md:grid-cols-[repeat(15,minmax(0,1fr))] gap-4 md:gap-5 md:auto-rows-[minmax(200px,auto)]">
            <ScrollReveal direction="left" distance={70} className="md:col-span-9 md:row-span-2 h-full">
              <FeaturedCard p={featured} />
            </ScrollReveal>

            {medium.map((p, i) => (
              <ScrollReveal key={p.id} delay={(i + 1) * 90} direction="right" distance={70} className="md:col-span-6 h-full">
                <MediumCard p={p} />
              </ScrollReveal>
            ))}

            {small.map((p, i) => (
              <ScrollReveal
                key={p.id}
                delay={(i + 3) * 90}
                direction={i % 2 === 0 ? "left" : "right"}
                distance={70}
                className="md:col-span-5 h-full"
              >
                <SmallCard p={p} />
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ShowcaseSection;
